import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../pixiSettings";

import { SOUND_NAME } from "../../../sounds";
import anime from "animejs/lib/anime.es.js";
import WinAnimationSecondaryController from "./WinAnimationSecondaryController";
import {
  selectWinAnimationsPlaying,
  selectBet,
  selectCurrentRound,
  selectFreeSpinActive,
} from "../../../State/selectors";
import { soundManager } from "../../../SoundManager";
import { attemptFullscreenToggle } from "../../../utilities";



const WinAnimationSecondary = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const winControllerRef = useRef(null);
  const winContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const handleUserDismissRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;


  // Overlay state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef (same API as Overlay component)
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: POPUP_OVERLAY_CONFIG.ALPHA,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: POPUP_OVERLAY_CONFIG.ALPHA },
        alpha: 0,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Get win animations playing state and actual game data
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const bet = useSelector(selectBet);
  const currentRound = useSelector(selectCurrentRound);
  const freeSpinActive = useSelector(selectFreeSpinActive);

  // Use win amount from current round (same format for base game and free spins)
  const winAmount = currentRound?.win || 0;

  // Initialize WinAnimationController when assets are ready
  useEffect(() => {
    if (winContainerRef.current && !winControllerRef.current) {
      winControllerRef.current = new WinAnimationSecondaryController(
        winContainerRef.current,
        dimensionsContext,
        overlayRef,
        dispatch,
        uiSymbolSize,
        uiSymbolSize,
        t,
      );
      // "TAP TO CONTINUE" text routes through the same dismiss flow (fullscreen
      // + sound + skip/close) as the full-screen overlay. Proxy via ref so it
      // always calls the latest handleUserDismiss, not a stale mount-time closure.
      winControllerRef.current.setClickHandler((...args) => {
        if (handleUserDismissRef.current) handleUserDismissRef.current(...args);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (winControllerRef.current && uiSymbolSize && dimensionsContext) {
      winControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Handle user click: fullscreen (if needed) + continue animation
  const handleUserDismiss = useCallback(
    async (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      await attemptFullscreenToggle(dispatch, device);

      if (winControllerRef.current && winAnimationsPlaying) {
        const accepted = winControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [winAnimationsPlaying, dispatch, device],
  );

  // Keep ref current so the controller's proxy always calls the latest version
  handleUserDismissRef.current = handleUserDismiss;

  // Add spacebar handling - ensure this takes priority over other handlers
  useEffect(() => {
    const handleKeyDown = async (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when animations are playing AND controller exists
      if (!winAnimationsPlaying || !winControllerRef.current) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Emergency recovery check
      try {
        await handleUserDismiss(event);
      } catch (error) {
        // Emergency recovery - try to finish animation
        if (
          winControllerRef.current &&
          winControllerRef.current.finishAnimation
        ) {
          winControllerRef.current.finishAnimation();
        }
      }
    };

    // Use capture phase to ensure we get the event first
    if (winAnimationsPlaying) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [winAnimationsPlaying, handleUserDismiss]);

  // Start animation when winAnimationsPlaying becomes true and we have a valid win
  useEffect(() => {
    if (winAnimationsPlaying && winControllerRef.current && winAmount > 0) {
      // Use the bet stored inside the round (the bet that actually produced this win).
      // Falls back to current Redux bet for live spins where round.bet may not be set yet.
      const roundBet = currentRound?.bet || bet;
      winControllerRef.current.startSequence(winAmount, roundBet);
    } else if (!winAnimationsPlaying && winControllerRef.current) {
      // Stop animation when state becomes false
      winControllerRef.current.stopWinAnimation();
    }
  }, [
    winAnimationsPlaying,
    winAmount,
    bet,
    freeSpinActive,
    currentRound,
  ]);

  // Cleanup overlay animation on unmount
  useEffect(() => {
    return () => {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position to prevent recalculation on every render
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function (WarningPopup style)
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(POPUP_OVERLAY_CONFIG.COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="win_animation"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={10}
      eventMode={winAnimationsPlaying ? "auto" : "none"}
      ref={winContainerRef}
    >
      {/* Background overlay - inline Graphics (WarningPopup pattern) */}
      <Graphics
        name="win_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={winAnimationsPlaying ? "static" : "none"}
        pointerdown={handleUserDismiss}
        cursor="default"
      />

      {/* Win spine, counter, and button are managed internally by WinAnimationSecondaryController */}
    </Container>
  );
};

export default WinAnimationSecondary;
